<template>
  <el-dialog v-model="showPreview" width="80%">
    <div class="flex justify-center">
      <img v-if="isImage(currentAttachment?.filename)" class="el-upload-list__item-thumbnail"
           :src="getAttachmentUrl(currentAttachment)"
           alt="" width="80%">
      <embed v-if="isPDF(currentAttachment?.filename)" :src="getAttachmentUrl(currentAttachment)" width="100%" height="600px"/>
    </div>
  </el-dialog>

  <div class="flex flex-col gap-2 mb-5">
    <div v-if="vetServiceId && serviceItemId" class="flex flex-col gap-2">
      <ul class="flex flex-wrap el-upload-list el-upload-list--picture-card">
        <li :key="attachment.id"
            v-for="attachment in attachments"
            class="el-upload-list__item">
          <div class="w-[100px]">
            <template v-if="attachment.mimetype == 'image/png' || attachment.mimetype == 'image/jpeg'">
              <img class="el-upload-list__item-thumbnail"
                   :src="getAttachmentUrl(attachment)"
                   alt="">
            </template>
            <template v-else>
              <div class="w-full h-full flex items-center justify-center">
                <el-icon style="font-size: 36px">
                  <Document/>
                </el-icon>
              </div>
            </template>

            <label class="absolute left-[50%] translate-x-[-50%] bottom-0 bg-black/45 text-white p-1 w-[100%] truncate text-xs">
              {{ attachment.originalFilename }}
            </label>
            <span class="el-upload-list__item-actions">
              <span v-if="canBePreviewed(attachment.filename)" class="el-upload-list__item-preview">
                <el-icon @click="previewAttachment(attachment)">
                  <Icon icon="icon-park-outline:preview-open"/>
                </el-icon>
              </span>
              <span class="el-upload-list__item-preview">
                <el-icon @click="downloadAttachment(attachment)">
                  <Download/>
                </el-icon>
              </span>
              <span class="el-upload-list__item-delete">
                <el-icon @click="handleRemove(attachment)">
                  <Delete/>
                </el-icon>
              </span>
            </span>
          </div>
        </li>
        <li>
          <el-upload
              class="w-[100px] h-[100px]"
              drag
              multiple
              :show-file-list="false"
              :action="uploadUrl"
              :on-success="onUploadSuccess"
              :on-error="showError"
              :http-request="handleUpload"
          >
            <div class="m-[10px] text-lg">
              <el-icon>
                <Plus/>
              </el-icon>
            </div>
          </el-upload>
        </li>
      </ul>
    </div>
    <div v-else class="text-gray-400 text-sm">
      {{ t('saveToUpload') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useHTTP } from "@/plugins/http"
import { computed, onMounted, ref, watch } from "vue"
import { Delete, Document, Download, Plus } from '@element-plus/icons-vue'
import type { UploadFile, UploadFiles, UploadRequestOptions } from 'element-plus'
import { ElMessage } from 'element-plus'
import { useI18n } from "vue-i18n"
import { useSettingsStore } from "@/stores/settingsStore"
import VetService from "@/models/VetService"
import { Icon } from "@iconify/vue"
import { canBePreviewed, isImage, isPDF } from "@/lib/utils"

interface VetServiceAttachment {
  id: string
  vetServiceID: string
  serviceItemID: string
  filename: string
  originalFilename: string
  mimetype: string
  createdAt: string
  updatedAt: string
}

const props = defineProps<{
  vetServiceId: string
  serviceItemId: string
}>()

const emit = defineEmits(['update'])

const { t } = useI18n({
  messages: {
    en: {
      uploadAttachment: 'Upload Attachments',
      saveToUpload: 'Save the vet service first to upload attachments'
    },
    ar: {
      uploadAttachment: 'رفع مرفقات',
      saveToUpload: 'احفظ الخدمة البيطرية أولاً لرفع المرفقات'
    }
  }
})

const http = useHTTP()
const settingsStore = useSettingsStore()
const attachments = ref<VetServiceAttachment[]>([])
const currentAttachment = ref<VetServiceAttachment | null>(null)
const showPreview = ref(false)

const uploadUrl = computed(() => {
  return `${VetService.ENDPOINT}/${props.vetServiceId}/items/${props.serviceItemId}/attachments`
})

onMounted(async () => {
  if (props.vetServiceId && props.serviceItemId) {
    await loadAttachments()
  }
})

watch(() => [props.vetServiceId, props.serviceItemId], async ([newVetServiceId, newServiceItemId]) => {
  if (newVetServiceId && newServiceItemId) {
    await loadAttachments()
  }
}, { immediate: false })

async function loadAttachments() {
  if (!props.vetServiceId || !props.serviceItemId) return

  try {
    attachments.value = await http.get<VetServiceAttachment[]>(
      `${VetService.ENDPOINT}/${props.vetServiceId}/items/${props.serviceItemId}/attachments`
    )
  } catch (error) {
    console.error('Error loading attachments:', error)
    attachments.value = []
  }
}

function getAttachmentUrl(attachment: VetServiceAttachment | null) {
  if (!attachment) return ''
  return `${settingsStore.storagePath}/vet-services/${props.vetServiceId}/${attachment.filename}`
}

function previewAttachment(attachment: VetServiceAttachment) {
  currentAttachment.value = attachment
  showPreview.value = true
}

async function downloadAttachment(attachment: VetServiceAttachment) {
  try {
    const response = await fetch(getAttachmentUrl(attachment))
    if (!response.ok) throw new Error('Network response was not ok')
    const blob = await response.blob()
    const urlObject = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = urlObject
    link.download = attachment.originalFilename || 'downloaded-file'

    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(urlObject)
  } catch (error) {
    console.error('Error downloading the file:', error)
  }
}

async function handleRemove(attachment: VetServiceAttachment) {
  try {
    await http.delete(`${VetService.ENDPOINT}/attachments/${attachment.id}`)
    await loadAttachments()
    emit('update')
  } catch (error: any) {
    ElMessage({
      showClose: true,
      message: error?.title || 'Error deleting attachment',
      type: 'error',
    })
  }
}

function handleUpload(options: UploadRequestOptions) {
  const data = new FormData()
  data.set('file', options.file)
  return http.put(options.action, data)
}

function showError(error: any, uploadFile: UploadFile, uploadFiles: UploadFiles) {
  ElMessage({
    showClose: true,
    message: error?.title || 'Error uploading file',
    type: 'error',
  })
}

async function onUploadSuccess() {
  await loadAttachments()
  emit('update')
}
</script>

<style scoped>
.el-upload-list__item {
  width: 100px !important;
  height: 100px !important;
}
</style>
